import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ClientConsultNoteTask } from '../../../core/models/client-consult-note-task.model';
import { NavigationService } from '../../../core/services/navigation.service';
import { ClientConsultNoteTasksService } from '../../../core/services/client-consult-note-tasks.service';
import { ClientConsultNoteTaskChoice } from '../../../core/enums/client-consult-note-task-choice.enum';
import { AppToastService } from 'app/core/services/app-toast.service';
import { Prescription } from '../../../core/models/prescription.model';
import { MeService } from '../../../core/services/me.service';
import { ClientConsultNoteTaskStatus } from '../../../core/enums/client-consult-note-task-status.enum';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { FormsModule } from '@angular/forms';

import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

const isSubmitAllowed = (task: ClientConsultNoteTask) => (
    task.status === ClientConsultNoteTaskStatus.open &&
    (task.choice === ClientConsultNoteTaskChoice.yes ||
      task.choice === ClientConsultNoteTaskChoice.no ||
      task.choice === ClientConsultNoteTaskChoice.assignBackToPar8o ||
      task.choice === ClientConsultNoteTaskChoice.prescriberIsCeProvider)
  );

const isSubmitEnabled = (task: ClientConsultNoteTask, prescriptions: Prescription[]) => {
  if (task.status === ClientConsultNoteTaskStatus.open) {
    if (task.choice === ClientConsultNoteTaskChoice.yes) {
      return prescriptions.some(p => p.nearestClientConsultNoteVerification && !p.clientVerifiedConsultNote);
    } else if (
      task.choice === ClientConsultNoteTaskChoice.no ||
      task.choice === ClientConsultNoteTaskChoice.assignBackToPar8o ||
      task.choice === ClientConsultNoteTaskChoice.prescriberIsCeProvider
    ) {
      return true;
    }
  }
  return false;
};

@Component({
    selector: 'app-client-consult-note-task-actions',
    templateUrl: './client-consult-note-task-actions.component.html',
    styleUrls: ['./client-consult-note-task-actions.component.scss'],
    imports: [FormsModule, MatProgressSpinner, FaIconComponent]
})
export class ClientConsultNoteTaskActionsComponent implements OnInit, OnChanges {
  private navigationService = inject(NavigationService);
  private clientConsultNoteTasksService = inject(ClientConsultNoteTasksService);
  private authService = inject(AuthenticationService);
  private meService = inject(MeService);
  private toastService = inject(AppToastService);

  submitAllowed = false;
  submitEnabled = false;
  submitLoading = false;
  formModel = { notes: null };
  nextButtonEnabled: boolean;
  nextLoading = false;
  serverError = false;
  serverErrorMsg = null;

  @Input() task: ClientConsultNoteTask;
  @Input() prescriptions: Prescription[];
  @Input() formDisabled : boolean;
  @Output() taskChange = new EventEmitter<ClientConsultNoteTask>();

  constructor() {
    this.nextButtonEnabled = !this.authService.isCaptureAdminUser;
  }

  ngOnInit() {
    this.init();
  }

  ngOnChanges() {
    this.init();
  }

  onSubmitClick($event) {
    $event.preventDefault();
    this.serverError = false;
    this.submitLoading = true;

    this.clientConsultNoteTasksService.submit(this.task.id)
      .pipe(
        finalize(() => {
          this.submitLoading = false;
        })
      )
      .subscribe(
        result => this.handleSuccessfulSubmission(result),
        error => this.handleFailedSubmissionOrNext(error)
      );

  }

  private handleSuccessfulSubmission(task: ClientConsultNoteTask) {
    this.meService.notifyUserTodosChanged();
    this.taskChange.emit(task);
    this.toastService.show('Task was successfully submitted to R1', { cssClass: 'bg-success-subtle' });
  }

  private handleFailedSubmissionOrNext(error) {
    this.serverError = true;
    this.serverErrorMsg = error.error['errors'];
  }

  onNextClick($event) {
    $event.preventDefault();

    this.serverError = false;
    this.nextLoading = true;

    this.clientConsultNoteTasksService.skipToNextTask(this.task)
      .pipe(
        finalize(() => {
          this.nextLoading = false;
        })
      )
      .subscribe(
        result => this.goToNextTask(result.item),
        error => this.handleFailedSubmissionOrNext(error)
      );
  }

  private init() {
    this.submitAllowed = !this.formDisabled && isSubmitAllowed(this.task);
    this.submitEnabled = isSubmitEnabled(this.task, this.prescriptions);
    this.formModel = { notes: this.task.notes };
  }

  onNotesChange() {
    this.updateNotes(this.formModel.notes);
  }

  private updateNotes(notes: string) {
    this.clientConsultNoteTasksService.updateNotes(this.task.id, notes).subscribe(result => {
      this.taskChange.emit(result);
    });
  }

  private goToNextTask(item) {
    const baseTaskPath = "/ce-portal/tasks/client-consult-note-tasks";
    if (item) {
      this.navigationService.navigateTo(`${baseTaskPath}/${item.id}`);
    } else {
      this.navigationService.navigateTo(baseTaskPath);
    }
  }

  public get additionalDetailsRequired() {
    return this.task.choice === ClientConsultNoteTaskChoice.no;
  }
}
